/**
 * Markdown to a small tree, for `Markdown.tsx` to render as React nodes.
 *
 * The house approach (website-main's `web/src/lib/chat-markdown.tsx`, itself
 * a port of the docs site's Ask AI): model output is never parsed as HTML, so
 * there is no `dangerouslySetInnerHTML` and no sanitiser to keep current. A
 * pattern this does not know renders as the characters the model wrote — a
 * worse-looking answer, never a security problem.
 *
 * Assist is a general chat rather than a docs helper, so it covers more than
 * the house subset: headings, fenced code, nested lists, block quotes, simple
 * tables and rules, and links to any `https:` address (the docs helper only
 * allowed its own site). It stays a pure function so it can be tested without
 * a DOM, and it tolerates the half-written text a stream produces — an open
 * fence is a code block that has not finished yet.
 */

export type Inline =
  | { t: 'text'; v: string }
  | { t: 'code'; v: string }
  | { t: 'strong' | 'em' | 'del'; c: Inline[] }
  | { t: 'link'; href: string; c: Inline[] }
  | { t: 'br' };

export type Align = 'left' | 'center' | 'right' | null;

export type Block =
  | { t: 'p'; c: Inline[] }
  | { t: 'h'; level: number; c: Inline[] }
  | { t: 'code'; lang: string; text: string }
  | { t: 'quote'; c: Block[] }
  | { t: 'list'; ordered: boolean; start: number; items: Block[][] }
  | { t: 'table'; align: Align[]; head: Inline[][]; rows: Inline[][][] }
  | { t: 'hr' };

// ── inline ──────────────────────────────────────────────────────────────────

/**
 * One pass, leftmost match wins, and at the same position the earlier
 * alternative wins — so `**` is tried before `*`, and a markdown link before
 * the bare address inside it. Groups, in order: code, link, strong (`**`,
 * `__`), strikethrough, emphasis (`*`, `_`), a `<br>` tag, bare https
 * address. `_` emphasis refuses to start or end inside a word, so
 * `snake_case_names` stay literal.
 *
 * `<br>`, `<br/>` and `<br />`, in any case, are the one piece of HTML read:
 * models put them in table cells, where a newline would end the row. Each
 * becomes the line break a newline makes; every other tag stays text.
 */
const INLINE =
  /(`[^`\n]+`)|(\[[^\]\n]+\]\([^)\s]+\))|(\*\*(?=\S)[\s\S]*?\S\*\*|__(?=\S)[\s\S]*?\S__)|(~~(?=\S)[\s\S]*?\S~~)|(\*(?=[^\s*])[^*\n]*?[^\s*]\*|(?<![\w\\])_(?=[^\s_])[^_\n]*?[^\s_]_(?!\w))|(<[bB][rR] *\/?>)|(https:\/\/[^\s<>]*[^\s<>.,;:!?'")\]}*_~])/g;

/** Only `https:` leaves as a link. Anything else — `javascript:`, `data:`,
 *  plain `http:`, a relative path that means nothing here — renders as its
 *  label. */
export function isSafeHref(href: string): boolean {
  if (!/^https:\/\/\S+$/i.test(href)) return false;
  try {
    return new URL(href).protocol === 'https:';
  } catch {
    return false;
  }
}

function pushText(out: Inline[], text: string) {
  // A newline inside a paragraph is a line break: people and models both
  // write addresses, verses and short stacked lines that way.
  text.split('\n').forEach((part, i) => {
    if (i > 0) out.push({ t: 'br' });
    if (part) out.push({ t: 'text', v: part });
  });
}

export function parseInline(text: string): Inline[] {
  const out: Inline[] = [];
  let last = 0;

  for (const m of text.matchAll(INLINE)) {
    const index = m.index ?? 0;
    const token = m[0];
    if (index > last) pushText(out, text.slice(last, index));

    if (m[1]) {
      out.push({ t: 'code', v: token.slice(1, -1) });
    } else if (m[2]) {
      const split = token.indexOf('](');
      const label = parseInline(token.slice(1, split));
      const href = token.slice(split + 2, -1);
      if (isSafeHref(href)) out.push({ t: 'link', href, c: label });
      else out.push(...label);
    } else if (m[3]) {
      out.push({ t: 'strong', c: parseInline(token.slice(2, -2)) });
    } else if (m[4]) {
      out.push({ t: 'del', c: parseInline(token.slice(2, -2)) });
    } else if (m[5]) {
      out.push({ t: 'em', c: parseInline(token.slice(1, -1)) });
    } else if (m[6]) {
      out.push({ t: 'br' });
    } else if (isSafeHref(token)) {
      out.push({ t: 'link', href: token, c: [{ t: 'text', v: token }] });
    } else {
      pushText(out, token);
    }

    last = index + token.length;
  }

  if (last < text.length) pushText(out, text.slice(last));
  return out;
}

// ── blocks ──────────────────────────────────────────────────────────────────

const FENCE_OPEN = /^( {0,3})(`{3,}|~{3,})[ \t]*([^`\s]*)/;
const FENCE_CLOSE = /^ {0,3}(`{3,}|~{3,})[ \t]*$/;
const HEADING = /^ {0,3}(#{1,6})[ \t]+(.*?)(?:[ \t]+#+)?[ \t]*$/;
const RULE = /^ {0,3}([-*_])(?:[ \t]*\1){2,}[ \t]*$/;
const QUOTE = /^ {0,3}> ?(.*)$/;
const BULLET = /^( *)([-*+])[ \t]+(.*)$/;
const ORDERED = /^( *)(\d{1,9})[.)][ \t]+(.*)$/;
const TABLE_RULE = /^ *\|? *:?-+:? *(?:\| *:?-+:? *)*\|? *$/;

interface Marker {
  indent: number;
  ordered: boolean;
  num: number;
  content: string;
  /** Where an item's own text starts, which is how far its continuation
   *  lines are dedented before being parsed as the item's blocks. */
  contentIndent: number;
}

function marker(line: string): Marker | null {
  const b = BULLET.exec(line);
  if (b) {
    return { indent: b[1]!.length, ordered: false, num: 1, content: b[3]!, contentIndent: b[1]!.length + 2 };
  }
  const o = ORDERED.exec(line);
  if (o) {
    return {
      indent: o[1]!.length,
      ordered: true,
      num: Number.parseInt(o[2]!, 10),
      content: o[3]!,
      contentIndent: o[1]!.length + o[2]!.length + 2
    };
  }
  return null;
}

const indentOf = (line: string): number => line.length - line.trimStart().length;
const blank = (line: string | undefined): boolean => !line || !line.trim();

function dedent(line: string, by: number): string {
  let n = 0;
  while (n < by && line[n] === ' ') n++;
  return line.slice(n);
}

function isTableStart(lines: string[], i: number): boolean {
  const head = lines[i];
  const rule = lines[i + 1];
  return !!head && !!rule && head.includes('|') && rule.includes('-') && rule.includes('|') && TABLE_RULE.test(rule);
}

/** A line that ends a paragraph without a blank line before it. Lists
 *  interrupt too, because models write "Options:\n- one\n- two" far more often
 *  than they mean a paragraph that happens to start with a dash; an ordered
 *  list only when it starts at 1, so a sentence ending "…in\n2024. " stays one. */
function interrupts(lines: string[], i: number): boolean {
  const line = lines[i]!;
  if (FENCE_OPEN.test(line) || HEADING.test(line) || RULE.test(line) || QUOTE.test(line)) return true;
  if (isTableStart(lines, i)) return true;
  const m = marker(line);
  return !!m && (!m.ordered || m.num === 1);
}

function splitRow(line: string): string[] {
  let row = line.trim();
  if (row.startsWith('|')) row = row.slice(1);
  if (row.endsWith('|') && !row.endsWith('\\|')) row = row.slice(0, -1);
  return row.split(/(?<!\\)\|/).map((cell) => cell.replace(/\\\|/g, '|').trim());
}

function alignOf(cell: string): Align {
  const left = cell.startsWith(':');
  const right = cell.endsWith(':');
  if (left && right) return 'center';
  if (right) return 'right';
  if (left) return 'left';
  return null;
}

function parseLines(lines: string[]): Block[] {
  const out: Block[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i]!;
    if (blank(line)) {
      i++;
      continue;
    }

    // Fenced code. Unclosed runs to the end: mid-stream, that is simply a
    // code block the model has not finished.
    const fence = FENCE_OPEN.exec(line);
    if (fence) {
      const open = fence[2]!;
      const indent = fence[1]!.length;
      const body: string[] = [];
      i++;
      while (i < lines.length) {
        const close = FENCE_CLOSE.exec(lines[i]!);
        if (close && close[1]![0] === open[0] && close[1]!.length >= open.length) {
          i++;
          break;
        }
        body.push(dedent(lines[i]!, indent));
        i++;
      }
      out.push({ t: 'code', lang: fence[3] ?? '', text: body.join('\n') });
      continue;
    }

    const heading = HEADING.exec(line);
    if (heading) {
      out.push({ t: 'h', level: heading[1]!.length, c: parseInline(heading[2]!) });
      i++;
      continue;
    }

    // Before lists, so `- - -` and `***` are rules rather than list items.
    if (RULE.test(line)) {
      out.push({ t: 'hr' });
      i++;
      continue;
    }

    if (QUOTE.test(line)) {
      const inner: string[] = [];
      while (i < lines.length) {
        const q = QUOTE.exec(lines[i]!);
        if (!q) break;
        inner.push(q[1]!);
        i++;
      }
      out.push({ t: 'quote', c: parseLines(inner) });
      continue;
    }

    if (isTableStart(lines, i)) {
      const head = splitRow(line);
      const align = splitRow(lines[i + 1]!).map(alignOf);
      const width = head.length;
      i += 2;
      const rows: Inline[][][] = [];
      while (i < lines.length && !blank(lines[i]) && lines[i]!.includes('|')) {
        const cells = splitRow(lines[i]!);
        rows.push(Array.from({ length: width }, (_, c) => parseInline(cells[c] ?? '')));
        i++;
      }
      out.push({
        t: 'table',
        align: Array.from({ length: width }, (_, c) => align[c] ?? null),
        head: head.map((cell) => parseInline(cell)),
        rows
      });
      continue;
    }

    const first = marker(line);
    if (first) {
      const items: string[][] = [];
      let current: string[] = [first.content];
      let contentIndent = first.contentIndent;
      i++;

      while (i < lines.length) {
        const l = lines[i]!;

        if (blank(l)) {
          // A blank line continues the list only if what follows it is
          // another item at this level or something indented under this one.
          let j = i + 1;
          while (j < lines.length && blank(lines[j])) j++;
          const next = lines[j];
          if (next === undefined) {
            i = j;
            break;
          }
          const nm = marker(next);
          const sibling = nm && nm.ordered === first.ordered && nm.indent <= first.indent + 1;
          if (!sibling && indentOf(next) <= first.indent) break;
          for (let k = i; k < j; k++) current.push('');
          i = j;
          continue;
        }

        const m = marker(l);
        if (m && m.indent <= first.indent + 1 && m.indent < contentIndent) {
          // A marker at this list's level: another item, or — a different
          // kind of list — the end of this one.
          if (m.ordered !== first.ordered) break;
          items.push(current);
          current = [m.content];
          contentIndent = m.contentIndent;
          i++;
          continue;
        }

        if (indentOf(l) > first.indent) {
          current.push(dedent(l, contentIndent));
          i++;
          continue;
        }

        // Unindented text straight after an item is a lazy continuation of
        // it, unless it starts a block of its own.
        if (interrupts(lines, i)) break;
        current.push(l.trim());
        i++;
      }
      items.push(current);

      out.push({ t: 'list', ordered: first.ordered, start: first.num, items: items.map(parseLines) });
      continue;
    }

    // A paragraph: every line until a blank one or a block that interrupts.
    const para: string[] = [line.trim()];
    i++;
    while (i < lines.length && !blank(lines[i]) && !interrupts(lines, i)) {
      para.push(lines[i]!.trim());
      i++;
    }
    out.push({ t: 'p', c: parseInline(para.join('\n')) });
  }

  return out;
}

export function parseMarkdown(text: string): Block[] {
  return parseLines(text.replace(/\r\n?/g, '\n').replace(/\t/g, '    ').split('\n'));
}
